import type { CountryResult, SourceRef } from "../../../../shared/contracts/query";

type RateLine = CountryResult["rates"][number];
type DocumentItem = CountryResult["documents"][number];
type TradeMeasure = CountryResult["measures"][number];

export type CountryDetailProps = {
  result: CountryResult;
  sources: readonly SourceRef[];
};

const COUNTRY_HEADINGS: Record<CountryResult["country"], string> = {
  CN: "中国出口",
  US: "美国进口",
  CA: "加拿大进口",
};

const TRANSLATION_STATUS: Record<CountryResult["chineseExplanation"]["status"], string> = {
  machine: "机器翻译，待复核",
  human_reviewed: "人工审核",
  not_needed: "原文为中文",
};

const DOCUMENT_STATUS: Record<DocumentItem["status"], string> = {
  prepare_retain: "准备/留存",
  required_now: "申报时提交",
  conditional: "条件触发",
  on_request: "海关要求时提交",
  not_applicable: "不适用",
  manual_review: "人工复核",
};

const MEASURE_STATUS: Record<TradeMeasure["matchStatus"], string> = {
  not_indicated: "未提示",
  possible: "可能匹配",
  confirmed_by_rule: "规则确认",
  manual_review: "人工复核",
};

function chineseName(names: CountryResult["legalNames"]): string | null {
  return names.find((name) => name.language.toLowerCase().startsWith("zh"))?.text ?? null;
}

function rateStatus(rate: RateLine): string {
  if (rate.confirmed && rate.includedInConfirmedTotal) return "已确认并计入合计";
  if (rate.confirmed) return "已确认，未计入合计";
  if (rate.category === "additional_duty" || rate.category === "trade_remedy") return "可能适用，未计入合计";
  return "待人工复核，未计入合计";
}

function totalLabel(value: string | null): string {
  if (value === null) return "人工复核";
  return value.endsWith("%") ? value : `${value}%`;
}

function relevantSources(result: CountryResult, sources: readonly SourceRef[]): SourceRef[] {
  const ids = new Set([
    ...result.legalNames.map((name) => name.sourceId),
    ...result.hierarchy.flatMap((node) => node.legalNames.map((name) => name.sourceId)),
    ...result.chineseExplanation.basedOnSourceIds,
    ...result.classificationSourceIds,
    ...result.rates.map((rate) => rate.sourceId),
    ...result.documents.map((document) => document.sourceId),
    ...result.measures.map((measure) => measure.sourceId),
  ]);
  return sources.filter((source) => ids.has(source.id));
}

export function CountryDetail({ result, sources }: CountryDetailProps) {
  const countryHeading = COUNTRY_HEADINGS[result.country];
  const heading = result.hierarchy.find((node) => node.codeDigits === 4);
  const legalName = chineseName(result.legalNames) ?? result.chineseExplanation.text;
  const documents = result.documents.filter((document) => document.status !== "not_applicable");
  const measures = result.measures.filter((measure) => measure.matchStatus !== "not_indicated");
  const warnings = [...new Set(result.warnings)];
  const sourceList = relevantSources(result, sources);

  return (
    <section
      id={`country-detail-${result.country}`}
      className="country-detail-panel country-detail-view"
      role="region"
      aria-label={`${countryHeading}详情`}
    >
      <header className="detail-heading">
        <p className="eyebrow">提示摘要</p>
        <h3>{countryHeading}提示摘要</h3>
      </header>

      <section className="detail-block" aria-labelledby={`detail-classification-${result.country}`}>
        <h4 id={`detail-classification-${result.country}`}>归类摘要</h4>
        <dl className="detail-fields">
          <div><dt>候选编码</dt><dd className="detail-code-hierarchy">{result.displayCode}</dd></div>
          {heading ? (
            <div>
              <dt>章节与大类</dt>
              <dd>
                <strong>第{heading.code.slice(0, 2)}章 · 税目 {heading.displayCode}</strong>
                <span className="detail-secondary">{chineseName(heading.legalNames) ?? "中文释义暂未生成"}</span>
              </dd>
            </div>
          ) : null}
          <div><dt>法定品名（中文释义）</dt><dd>{legalName}</dd></div>
          <div><dt>释义状态</dt><dd>{TRANSLATION_STATUS[result.chineseExplanation.status]}</dd></div>
          <div><dt>归类结论</dt><dd>{result.classificationReason}</dd></div>
        </dl>
      </section>

      <section className="detail-block" aria-labelledby={`detail-rates-${result.country}`}>
        <h4 id={`detail-rates-${result.country}`}>税费摘要</h4>
        {result.rates.length > 0 ? (
          <ul className="detail-summary-list">
            {result.rates.map((rate) => (
              <li key={rate.id}>
                <span><strong>{rate.label}</strong><small>{rateStatus(rate)}</small></span>
                <b>{rate.displayValue}</b>
              </li>
            ))}
          </ul>
        ) : <p className="detail-empty">未提供税率行；不代表税率为零。</p>}
        <p className="confirmed-total detail-total">已确认税率合计：{totalLabel(result.confirmedTotalPercent)}</p>
      </section>

      <section className="detail-block" aria-labelledby={`detail-documents-${result.country}`}>
        <h4 id={`detail-documents-${result.country}`}>文件动作</h4>
        {documents.length > 0 ? (
          <ul className="detail-action-list">
            {documents.map((document) => (
              <li key={document.id}>
                <strong>{document.label}</strong>
                <span>{DOCUMENT_STATUS[document.status]}：{document.reason}</span>
                {document.conditions.length > 0 ? <small>{document.conditions.join("；")}</small> : null}
              </li>
            ))}
          </ul>
        ) : <p className="detail-empty">暂无需要准备的文件提示。</p>}
      </section>

      <section className="detail-block" role="region" aria-label="风险提示">
        <h4>风险提示</h4>
        {measures.length > 0 ? (
          <ul className="detail-action-list">
            {measures.map((measure) => (
              <li key={measure.id}>
                <strong>{measure.label}<span className="detail-status">{MEASURE_STATUS[measure.matchStatus]}</span></strong>
                <span>{measure.legalScope}</span>
              </li>
            ))}
          </ul>
        ) : <p className="detail-empty">当前未提示明确贸易措施；仍需按官方规则复核。</p>}
        {warnings.length > 0 ? <ul className="detail-warning-list">{warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul> : null}
      </section>

      <section className="detail-block" aria-labelledby={`detail-sources-${result.country}`}>
        <h4 id={`detail-sources-${result.country}`}>来源</h4>
        {sourceList.length > 0 ? (
          <ul className="detail-source-list">
            {sourceList.map((source) => (
              <li key={source.id}>
                <a href={source.officialUrl} target="_blank" rel="noreferrer">{source.authority} · 查看来源</a>
              </li>
            ))}
          </ul>
        ) : <p className="detail-empty">未提供来源记录。</p>}
      </section>

      <p className="detail-disclosure">数据仅供参考，核对后才能用于报关，不能直接作为报关或税费结论。</p>
    </section>
  );
}
